import { useState } from 'react';
import type { CallDetail, CallLeg, CallQualityReading, CallStep, Directory, QualityRating } from '@stitch/pbx-adapter';
import { lookup } from '@stitch/pbx-adapter/describe';
import { formatPhoneNumber } from '@stitch/pbx-adapter/catalog';
import { api } from '../api/client';
import { clockTime, fullWhen, shownNumber } from '../lib/format';
import { Icon, type IconName } from '../lib/icons';
import { useData } from '../lib/useData';
import { Chip, LoadError, Loading } from './ui';
import { RecordingTranscripts } from './RecordingTranscript';

const RATING: Record<QualityRating, { label: string; bars: number; tone: 'ok' | 'warn' | 'crit' }> = {
  excellent: { label: 'Excellent', bars: 4, tone: 'ok' },
  good: { label: 'Good', bars: 3, tone: 'ok' },
  fair: { label: 'Fair', bars: 2, tone: 'warn' },
  poor: { label: 'Poor', bars: 1, tone: 'crit' }
};

function rate(mos: number): QualityRating {
  return mos >= 4 ? 'excellent' : mos >= 3.6 ? 'good' : mos >= 3.1 ? 'fair' : 'poor';
}

/** Four bars and a word: how a call (or one side of it) sounded. */
export function QualityBadge({ mos, compact }: { mos: number; compact?: boolean }) {
  const r = RATING[rate(mos)];
  return (
    <span className={`quality ${r.tone}`} title={`Call quality score ${mos.toFixed(1)} out of 5`}>
      <span className="quality-bars" aria-hidden="true">
        {[1, 2, 3, 4].map((i) => (
          <span key={i} className={i <= r.bars ? 'on' : ''} />
        ))}
      </span>
      <span>
        {r.label}
        {!compact && <span className="muted"> · {mos.toFixed(1)}</span>}
      </span>
    </span>
  );
}

const STEP: Record<string, { tag: string; icon: IconName; tone: string }> = {
  callQueue: { tag: 'Call queue', icon: 'headset', tone: 'queue' },
  autoAttendant: { tag: 'Auto attendant', icon: 'flow', tone: 'menu' },
  ringGroup: { tag: 'Ring group', icon: 'users', tone: 'group' },
  voiceAgent: { tag: 'Voice agent', icon: 'spark', tone: 'menu' }
};

const secs = (n: number | undefined) => (n === undefined ? '' : n < 60 ? `${n}s` : `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`);
const span = (from: string | undefined, to: string | undefined) => (from && to ? Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 1000)) : undefined);
const SIP_TEXT: Record<number, string> = { 486: 'busy', 487: 'cancelled', 480: 'unavailable', 404: 'not found', 603: 'declined', 408: 'no answer' };

/** One call in full, for a company's admins (and Stitch staff, who also see the phone system's trace). */
export function CallDetailView({ domain, record, dir }: { domain: string; record: string; dir: Directory | undefined }) {
  const data = useData(() => api.callDetail(domain, record), [domain, record]);
  const [open, setOpen] = useState<number | null>(null);
  if (data.error) return <LoadError error={data.error} retry={data.reload} />;
  if (!data.data) return <Loading label="Loading the call" />;
  const c = data.data.call;
  const name = (ext: string | undefined, fallback?: string) => (ext ? fallback || lookup(dir, ext).name || ext : fallback || '');
  // A number as people write it ((443) 555-0123), or what an internal route is (2983).
  const pretty = (n: string) => (n ? (shownNumber(n) !== n ? shownNumber(n) : formatPhoneNumber(n) || n) : '');
  const partyName = (p: CallDetail['from']) => p.name || (p.number ? lookup(dir, p.number).name : '') || pretty(p.number) || 'Unknown';
  const trunk = c.legs.find((l) => l.kind === 'trunk');
  const phones = c.legs.filter((l) => l.kind === 'phone');
  const steps = [...c.steps].sort((a, b) => a.start.localeCompare(b.start));
  // Each phone it rang goes under the step it rang from: the last one that started before it.
  const under = new Map<number, CallLeg[]>();
  for (const l of phones) {
    let at = -1;
    steps.forEach((s, i) => {
      if (s.start && s.start <= l.start) at = i;
    });
    under.set(at, [...(under.get(at) ?? []), l]);
  }
  const t0 = Date.parse(c.start);

  const phoneBranch = (l: CallLeg, i: number) => {
    const answered = Boolean(l.answered);
    const rang = span(l.start, l.answered ?? l.end);
    const talked = span(l.answered, l.end);
    return (
      <li className="flow-branch" key={`${l.extension}-${i}`}>
        <span className={`flow-key ${answered ? 'ok' : 'any'}`} aria-label={answered ? 'Answered' : 'Not answered'} title={answered ? 'Answered' : 'Not answered'}>
          <Icon name={answered ? 'check' : 'x'} size={14} />
        </span>
        <div className="flow-target">
          <div className={`flow-card ${answered ? 'person' : 'other'}`}>
            <span className="flow-tag">
              <Icon name="phone" size={13} /> {l.device ? l.device.split(' ').slice(0, 2).join(' ') : 'Phone'}
            </span>
            <span className="flow-name">
              {name(l.extension, l.extensionName)} {l.extension && <span className="mono muted">{l.extension}</span>}
            </span>
            <span className="flow-detail">
              {answered ? `Answered after ${secs(rang)} · talked ${secs(talked)}` : `Rang ${secs(rang)}${l.sipCode && SIP_TEXT[l.sipCode] ? ` · ${SIP_TEXT[l.sipCode]}` : ''}, not answered`}
              {l.redirectedFrom?.number ? ` · forwarded from ${partyName(l.redirectedFrom)}` : ''}
            </span>
            {l.quality?.mos !== undefined && <QualityBadge mos={l.quality.mos} />}
          </div>
        </div>
      </li>
    );
  };

  const stepBranch = (s: CallStep, i: number) => {
    const meta = STEP[s.kind] ?? { tag: s.kind || 'Feature', icon: 'flow' as IconName, tone: 'other' };
    const bits = [s.menuSeconds ? `in the menu ${secs(s.menuSeconds)}` : '', s.ringSeconds !== undefined ? `waited ${secs(s.ringSeconds)}` : '', s.talkSeconds ? `talked ${secs(s.talkSeconds)}` : '', s.holdSeconds ? `on hold ${secs(s.holdSeconds)}` : '']
      .filter(Boolean)
      .join(' · ');
    return (
      <li className="flow-branch" key={`step-${i}`}>
        <span className="flow-key any">{i + 1}</span>
        <div className="flow-target">
          <div className={`flow-card ${meta.tone}`}>
            <span className="flow-tag">
              <Icon name={meta.icon} size={13} /> {meta.tag}
            </span>
            <span className="flow-name">
              {s.accountName || name(s.account)} <span className="mono muted">{s.account}</span>
            </span>
            {bits && <span className="flow-detail">{bits.charAt(0).toUpperCase() + bits.slice(1)}</span>}
            {s.answeredBy && <span className="flow-detail">Answered by {s.answeredByName || name(s.answeredBy)}</span>}
          </div>
          {(under.get(i) ?? []).length > 0 && (
            <div className="flow-sub">
              <span className="flow-when">Rang:</span>
              <ul className="flow-branches">{(under.get(i) ?? []).map(phoneBranch)}</ul>
            </div>
          )}
        </div>
      </li>
    );
  };

  const readings = c.legs.filter((l) => l.quality && Object.keys(l.quality).length);
  const legName = (l: CallLeg) => (l.kind === 'trunk' ? `Carrier${l.trunk ? ` (${l.trunk})` : ''}` : `${name(l.extension, l.extensionName)}’s phone`);
  const cell = (q: CallQualityReading | undefined, k: keyof CallQualityReading, unit: string) => (q?.[k] === undefined ? '–' : `${q[k]}${unit}`);

  return (
    <div className="call-detail">
      <div className="call-summary">
        <Chip small tone={c.missed ? 'crit' : 'neutral'}>
          {c.missed ? 'Missed' : c.direction === 'inbound' ? 'Incoming' : c.direction === 'outbound' ? 'Outgoing' : c.direction === 'internal' ? 'Internal' : 'Call'}
        </Chip>
        <span>{fullWhen(c.start)}</span>
        {c.durationSeconds !== undefined && !c.missed && <span className="muted">· {secs(c.durationSeconds)}</span>}
        {c.quality && <QualityBadge mos={c.quality.mos} />}
      </div>

      <section aria-labelledby="cd-path">
        <h3 id="cd-path" className="cd-head">
          How the call went
        </h3>
        <div className="flow">
          <div className="flow-start">
            <Icon name={c.direction === 'outbound' ? 'arrowOut' : c.direction === 'internal' ? 'arrowBoth' : 'arrowIn'} size={15} />
            {c.direction === 'outbound' ? 'Call to' : 'Call from'} <strong>{partyName(c.direction === 'outbound' ? c.to : c.from)}</strong>
            {/* The number beside the name, once: not again when there's no name (2983). */}
            {(c.direction === 'outbound' ? c.to : c.from).number && pretty((c.direction === 'outbound' ? c.to : c.from).number) !== partyName(c.direction === 'outbound' ? c.to : c.from) && (
              <span className="mono muted">{pretty((c.direction === 'outbound' ? c.to : c.from).number)}</span>
            )}
          </div>
          {c.direction === 'outbound' && (
            <div className="flow-greeting">
              <Icon name="phone" size={14} /> <span>Made by {partyName(c.from)}</span>
            </div>
          )}
          <ul className="flow-branches">
            {trunk && (
              <li className="flow-branch">
                <span className="flow-key any">
                  <Icon name="link" size={14} />
                </span>
                <div className="flow-target">
                  <div className="flow-card other">
                    <span className="flow-tag">
                      <Icon name="link" size={13} /> {trunk.direction === 'inbound' ? 'Came in on' : 'Went out on'}
                    </span>
                    <span className="flow-name">{trunk.trunk || 'The carrier'}</span>
                    <span className="flow-detail">
                      {c.direction === 'inbound' ? `Dialled ${partyName(c.to)}` : `To ${c.to.number ? shownNumber(c.to.number) : partyName(c.to)}`}
                      {trunk.sipCode && trunk.sipCode >= 300 && SIP_TEXT[trunk.sipCode] ? ` · ${SIP_TEXT[trunk.sipCode]}` : ''}
                    </span>
                    {trunk.quality?.mos !== undefined && <QualityBadge mos={trunk.quality.mos} />}
                  </div>
                </div>
              </li>
            )}
            {steps.map(stepBranch)}
            {(under.get(-1) ?? []).map(phoneBranch)}
          </ul>
          {c.recordings.length > 0 && (
            <div className="flow-greeting">
              <Icon name="mic" size={14} /> <span>Recorded</span>
            </div>
          )}
        </div>
      </section>

      {c.recordings.length > 0 && (
        <section aria-labelledby="cd-rec">
          <h3 id="cd-rec" className="cd-head">
            Recording
          </h3>
          {c.recordings.map((r) => (
            <div key={r.id} className="cd-rec">
              <small className="muted">
                {r.extensionName || name(r.extension)} · {r.time ? clockTime(r.time) : ''}
              </small>
              <audio controls preload="none" src={api.callRecordingUrl(domain, record, r.id)} aria-label="Call recording" />
            </div>
          ))}
          <RecordingTranscripts domain={domain} record={record} recordings={c.recordings} />
        </section>
      )}

      {readings.length > 0 && (
        <section aria-labelledby="cd-q">
          <h3 id="cd-q" className="cd-head">
            Call quality
          </h3>
          <div className="table-wrap">
            <table className="table compact">
              <thead>
                <tr>
                  <th scope="col">Side</th>
                  <th scope="col">Sound</th>
                  <th scope="col">Lost</th>
                  <th scope="col">Jitter</th>
                  <th scope="col">Delay</th>
                </tr>
              </thead>
              <tbody>
                {readings.map((l, i) => (
                  <tr key={i}>
                    <td>{legName(l)}</td>
                    <td>{l.quality?.mos !== undefined ? <QualityBadge mos={l.quality.mos} /> : '–'}</td>
                    <td className="mono">{cell(l.quality, 'lossPercent', '%')}</td>
                    <td className="mono">{cell(l.quality, 'jitterMs', ' ms')}</td>
                    <td className="mono">{cell(l.quality, 'delayMs', ' ms')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted small">Scores run from 1 to 5: 4 and above sounds excellent, below 3.1 people notice. Lost packets and jitter over about 30 ms make calls choppy.</p>
        </section>
      )}

      <details className="cd-more">
        <summary>Call details</summary>
        <dl className="cd-facts">
          <dt>Call ID</dt>
          <dd className="mono">{c.callId || '–'}</dd>
          <dt>Started</dt>
          <dd>{fullWhen(c.start)}</dd>
          {c.answered && (
            <>
              <dt>Answered</dt>
              <dd>{clockTime(c.answered)}</dd>
            </>
          )}
          {c.end && (
            <>
              <dt>Ended</dt>
              <dd>{clockTime(c.end)}</dd>
            </>
          )}
          {c.legs.map((l, i) => (
            <div key={i} className="cd-leg">
              <dt>{legName(l)}</dt>
              <dd>{[l.device, l.address, l.codec, l.sipCode ? `SIP ${l.sipCode}` : ''].filter(Boolean).join(' · ') || '–'}</dd>
            </div>
          ))}
        </dl>
      </details>

      {c.sip.length > 0 && (
        <details className="cd-more">
          <summary>SIP messages ({c.sip.length})</summary>
          <ol className="sip-ladder">
            {c.sip.map((m, i) => (
              <li key={i}>
                <button type="button" className="sip-line" aria-expanded={open === i} onClick={() => setOpen(open === i ? null : i)}>
                  <span className="mono muted">+{((Date.parse(m.time) - t0) / 1000).toFixed(2)}s</span>
                  <span className={`sip-what ${m.status ? (m.status >= 400 ? 'bad' : m.status >= 200 ? 'ok' : 'info') : 'req'}`}>{m.method ?? m.status}</span>
                  <span className="mono sip-path">
                    {m.from} → {m.to}
                  </span>
                </button>
                {open === i && <pre className="sip-text">{m.text}</pre>}
              </li>
            ))}
          </ol>
        </details>
      )}

      {c.log.length > 0 && (
        <details className="cd-more">
          <summary>Phone system trace ({c.log.length}) · Stitch staff</summary>
          <pre className="sip-text">{c.log.map((l) => `${l.time.slice(11, 23)} ${l.category.padEnd(9)} ${l.level} ${l.message}`).join('\n')}</pre>
        </details>
      )}
    </div>
  );
}
